import type {
  CardLink,
  DealStatus,
  LinkPriority,
  LinkStatus,
  SellerType,
  TrackedCard,
} from "@pokifolio/contracts";

import { RAW_CONDITION_LABELS } from "../research/format";
import { formatMoney } from "../search/format";

export const LINK_STATUS_LABELS: Readonly<Record<LinkStatus, string>> = {
  draft: "Not sent",
  waiting: "Waiting for admin",
  approved: "Approved",
  declined: "Declined",
};

export const LINK_STATUS_TONES: Readonly<Record<LinkStatus, "neutral" | "warning" | "success" | "danger">> = {
  draft: "neutral",
  waiting: "warning",
  approved: "success",
  declined: "danger",
};

/** "Near mint · used · scratches, whitening" */
export function describeLinkCondition(link: CardLink): string {
  const marks = [
    link.scratches && "scratches",
    link.whitening && "whitening",
    link.bends && "bends",
    link.surfaceDamage && "surface damage",
  ].filter((m): m is string => typeof m === "string");
  return [
    RAW_CONDITION_LABELS[link.condition],
    link.itemState === "new" ? "new" : "used",
    marks.length === 0 ? "no visible damage" : marks.join(", "),
  ].join(" · ");
}

export function describeLinkPrice(link: CardLink): string {
  if (link.price === null) return "Open to bids";
  const price = formatMoney({ amount: link.price, currency: link.currency });
  return link.currency === "USD" || link.priceUsd === null
    ? price
    : `${price} (≈ ${formatMoney({ amount: link.priceUsd, currency: "USD" })})`;
}

/** The Step column: "2 waiting · 1 approved", or "No listings yet". */
export function describeCardStep(card: TrackedCard): string {
  const c = card.statusCounts;
  const parts = [
    c.waiting > 0 && `${String(c.waiting)} waiting`,
    c.approved > 0 && `${String(c.approved)} approved`,
    c.declined > 0 && `${String(c.declined)} declined`,
    c.draft > 0 && `${String(c.draft)} not sent`,
  ].filter((p): p is string => typeof p === "string");
  return parts.length === 0 ? "No listings yet" : parts.join(" · ");
}

/** The admin's WhatsApp number from the build settings (digits only, country code first). */
export const ADMIN_WHATSAPP = (import.meta.env.VITE_ADMIN_WHATSAPP ?? "4712345678").replace(/\D/g, "");

/** "Ask admin" (D-65): WhatsApp opens with the listing's details filled in; the employee presses Send. */
export function whatsAppAskLink(cardName: string, link: CardLink, employee: string, pageUrl: string): string {
  const text = [
    `Hi, ${employee} here. Please check this listing for ${cardName}:`,
    `Website: ${link.website}`,
    `Price: ${describeLinkPrice(link)}`,
    `Condition: ${describeLinkCondition(link)}`,
    ...(link.notes === null ? [] : [`Notes: ${link.notes}`]),
    link.url === null ? `Where: ${describeSource(link)}` : `Listing: ${link.url}`,
    `Approve or decline in Pokefolio: ${pageUrl}`,
  ].join("\n");
  return `https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent(text)}`;
}

export const DEAL_STATUS_LABELS: Readonly<Record<DealStatus, string>> = {
  open: "Open",
  opened: "Opened",
  negotiating: "In negotiation",
  no_buy: "No buy",
  buying: "Buying now",
  purchased: "Purchased",
  closed: "Closed",
};

export const DEAL_STATUS_TONES: Readonly<
  Record<DealStatus, "neutral" | "brand" | "warning" | "success" | "danger">
> = {
  open: "brand",
  opened: "neutral",
  negotiating: "warning",
  no_buy: "danger",
  buying: "warning",
  purchased: "success",
  closed: "neutral",
};

export const PRIORITY_LABELS: Readonly<Record<LinkPriority, string>> = {
  top: "Top",
  high: "High",
  mid: "Mid",
  low: "Low",
};

export const PRIORITY_TONES: Readonly<Record<LinkPriority, "danger" | "warning" | "brand" | "neutral">> = {
  top: "danger",
  high: "warning",
  mid: "brand",
  low: "neutral",
};

export const SELLER_LABELS: Readonly<Record<SellerType, string>> = {
  shop: "Shop",
  private: "Private seller",
};

/** "eBay · Shop", or "Physical store · Outland Oslo, Storgata 1". */
export function describeSource(link: CardLink): string {
  if (link.physicalStore)
    return `Physical store · ${[link.storeName, link.storeAddress].filter((v) => v !== null).join(", ")}`;
  return `${link.platform} · ${SELLER_LABELS[link.sellerType]}`;
}
